import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserSessionState } from '../types/userSession';
import { sessionStorage } from '../services/sessionStorage';
import { useAuth } from '../contexts/FirebaseAuthContext';

interface UseUserSessionReturn {
  session: UserSessionState | null;
  updateSession: (updates: Partial<UserSessionState>) => void;
  clearSession: () => void;
  restoreSession: () => Promise<void>;
  isRestoring: boolean;
}

export const useUserSession = (): UseUserSessionReturn => {
  const [session, setSession] = useState<UserSessionState | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  const hasRestoredRef = useRef(false);

  // Update in-memory session only (don't save to storage)
  const updateSession = useCallback((updates: Partial<UserSessionState>) => {
    // This is kept for compatibility but doesn't save to storage
    // Actual saving happens only on page unload
    if (!userProfile) return;

    const updatedSession = {
      ...session,
      ...updates,
      userId: userProfile.uid,
      lastAccessedAt: new Date()
    } as UserSessionState;

    setSession(updatedSession);
  }, [session, userProfile]);

  // Clear session
  const clearSession = useCallback(async () => {
    await sessionStorage.clearSession();
    setSession(null);
    hasRestoredRef.current = false;
  }, []);

  // Restore session from storage
  const restoreSession = useCallback(async () => {
    if (!userProfile || hasRestoredRef.current) return;

    setIsRestoring(true);
    try {
      // Try to get session from localStorage first
      let storedSession = await sessionStorage.getSession();
      
      
      if (storedSession && storedSession.userId === userProfile.uid) {
        setSession(storedSession);
        
        // Only restore location if we're on home page or just logged in
        if (location.pathname === '/' || location.pathname === '/login') {
          // Navigate to the stored location
          if (storedSession.currentView.page === 'collections' && storedSession.currentView.collectionId) {
            // Validate that the collection still exists before navigating
            try {
              const path = `/collections/${storedSession.currentView.collectionId}`;
              
              // Try to verify collection exists (this will fail if deleted)
              // For now, we'll just navigate and let the CollectionPage handle the 404
              if (storedSession.currentView.problemId) {
                // Restore to the exact problem with expanded sections
                navigate(`${path}/problem/${storedSession.currentView.problemId}`, { 
                  state: { 
                    restored: true,
                    expandedSections: storedSession.uiState?.expandedSections,
                    scrollPosition: storedSession.uiState?.scrollPosition 
                  } 
                });
              } else {
                navigate(path, { state: { restored: true } });
              }
            } catch (error) {
              console.error('Failed to restore to collection, navigating to home:', error);
              await clearSession();
            }
          } else if (storedSession.currentView.page === 'problems') {
            navigate('/problems', { state: { restored: true } });
          }
        }
        
        hasRestoredRef.current = true;
      }
    } catch (error) {
      console.error('Error restoring session:', error);
      await clearSession();
    } finally {
      setIsRestoring(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile, navigate, location]);

  // Track current location but DON'T save to storage yet
  useEffect(() => {
    if (!userProfile) return;

    const path = location.pathname;
    let page: 'home' | 'collections' | 'problems' = 'home';
    let collectionId: string | undefined;
    let problemId: string | undefined;

    if (path.includes('/collections/')) {
      page = 'collections';
      const match = path.match(/\/collections\/([^/]+)/);
      collectionId = match?.[1];

      const problemMatch = path.match(/\/problem\/([^/]+)/);
      problemId = problemMatch?.[1];
    } else if (path === '/problems') {
      page = 'problems';
    }

    // Only update in-memory state, don't persist to storage
    setSession(current => ({
      ...current,
      userId: userProfile.uid,
      lastAccessedAt: new Date(),
      currentView: {
        page,
        collectionId,
        problemId
      }
    } as UserSessionState));
  }, [location, userProfile]);

  // Save session ONLY when user leaves the app
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (session && userProfile) {
        // Get current expanded sections from DOM (if on a problem page)
        const expandedSections: any = {};
        const problemCards = document.querySelectorAll('.problem-card');
        if (problemCards.length > 0) {
          // Check which sections are currently expanded
          expandedSections.description = !!document.querySelector('[data-section="description"][aria-expanded="true"]');
          expandedSections.algorithm = !!document.querySelector('[data-section="algorithm"][aria-expanded="true"]');
          expandedSections.code = !!document.querySelector('[data-section="code"][aria-expanded="true"]');
          expandedSections.walkthrough = !!document.querySelector('[data-section="walkthrough"][aria-expanded="true"]');
        }
        
        // Save the current state
        const finalSession = {
          ...session,
          uiState: {
            ...session.uiState,
            expandedSections,
            scrollPosition: window.scrollY
          }
        };
        
        // Save directly (bypass debouncing)
        sessionStorage.saveSession(finalSession);
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        handleBeforeUnload();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [session, userProfile]);

  // Restore session on mount if user is logged in
  useEffect(() => {
    if (userProfile && !hasRestoredRef.current) {
      restoreSession();
    }
  }, [userProfile, restoreSession]);

  // Clear session on logout
  useEffect(() => {
    if (!userProfile && hasRestoredRef.current) {
      clearSession();
    }
  }, [userProfile, clearSession]);

  return {
    session,
    updateSession,
    clearSession,
    restoreSession,
    isRestoring
  };
};